import { useState } from 'react';
import {
  Button,
  IconButton,
  TextField,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Add, MoreVert } from '@mui/icons-material';
import Stack from '@/components/Stack';
import { Body, Small, Caption } from '@/components/Typography';
import Badge from '@/components/Badge';
import Card from '@/components/Card';
import { ConsolidatedSkill } from '@/services/workersV2API';
import AddWorkExperienceModal from './AddWorkExperienceModal';
import UploadUniformModal from './UploadUniformModal';
import UploadCertificateModal from './UploadCertificateModal';
import ExpirationSection from './ExpirationSection';
import { axiosRubyClient } from '@/util/axios/axiosClient';

type Props = {
  skill: ConsolidatedSkill;
  workerId: number;
  onUpdate: () => void;
};

const SkillPanel = ({ skill, workerId, onUpdate }: Props) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUniformModal, setShowUniformModal] = useState(false);
  const [showCertificateModal, setShowCertificateModal] = useState(false);
  const [editingExperience, setEditingExperience] = useState<number | null>(
    null,
  );
  const [menuAnchor, setMenuAnchor] = useState<{
    element: HTMLElement;
    experienceId: number;
  } | null>(null);
  const [uniformMenuAnchor, setUniformMenuAnchor] =
    useState<HTMLElement | null>(null);
  const [certificateMenuAnchor, setCertificateMenuAnchor] = useState<{
    element: HTMLElement;
    certificateId: number;
  } | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [processingAction, setProcessingAction] = useState(false);

  // Dialog states
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    open: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });
  const [promptDialog, setPromptDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    value: string;
    onConfirm: (value: string) => void;
  }>({
    open: false,
    title: '',
    message: '',
    value: '',
    onConfirm: () => {},
  });
  const [alertDialog, setAlertDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
  }>({
    open: false,
    title: '',
    message: '',
  });
  const [deleteSkillDialog, setDeleteSkillDialog] = useState<{
    open: boolean;
    markAsIneligible: boolean;
  }>({
    open: false,
    markAsIneligible: true,
  });
  const [expiryDialog, setExpiryDialog] = useState<{
    open: boolean;
    certificateId: number | null;
    expiryDate: string;
  }>({
    open: false,
    certificateId: null,
    expiryDate: '',
  });

  const handleAddExperience = () => {
    setEditingExperience(null);
    setShowAddModal(true);
  };

  const handleExperienceAdded = () => {
    setShowAddModal(false);
    setEditingExperience(null);
    onUpdate();
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    experienceId: number,
  ) => {
    setMenuAnchor({ element: event.currentTarget, experienceId });
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleEditExperience = () => {
    if (menuAnchor) {
      setEditingExperience(menuAnchor.experienceId);
      setShowAddModal(true);
    }
    handleMenuClose();
  };

  const handleDeleteExperience = () => {
    if (!menuAnchor) return;
    handleMenuClose();

    setConfirmDialog({
      open: true,
      title: 'Delete Work Experience',
      message: 'Are you sure you want to delete this work experience?',
      onConfirm: async () => {
        try {
          const response = await axiosRubyClient.delete(
            `/api/admin/work_experiences/${menuAnchor.experienceId}`,
          );

          if (response.data.success) {
            onUpdate();
          } else {
            setAlertDialog({
              open: true,
              title: 'Error',
              message: 'Failed to delete work experience',
            });
          }
        } catch (error) {
          console.error('Error deleting work experience:', error);
          setAlertDialog({
            open: true,
            title: 'Error',
            message: 'Failed to delete work experience',
          });
        }
      },
    });
  };

  const handleToggleRejection = () => {
    if (!skill.skill_request) return;

    const isCurrentlyRejected = !!skill.skill_request.rejected_at;
    const action = isCurrentlyRejected
      ? 'restore eligibility for'
      : 'mark as ineligible';

    setConfirmDialog({
      open: true,
      title: isCurrentlyRejected ? 'Restore Eligibility' : 'Mark as Ineligible',
      message: `Are you sure you want to ${action} this skill request?`,
      onConfirm: async () => {
        setRejecting(true);

        try {
          const response = await axiosRubyClient.patch(
            `/api/admin/skill_requests/${skill.skill_request!.id}/reject`,
            {
              rejected: !isCurrentlyRejected,
              rejection_comment: isCurrentlyRejected
                ? null
                : 'Marked as ineligible by admin',
            },
          );

          if (response.data.success) {
            onUpdate();
          } else {
            setAlertDialog({
              open: true,
              title: 'Error',
              message: `Failed to ${action} skill request`,
            });
          }
        } catch (error) {
          console.error(`Error ${action} skill request:`, error);
          setAlertDialog({
            open: true,
            title: 'Error',
            message: `Failed to ${action} skill request`,
          });
        } finally {
          setRejecting(false);
        }
      },
    });
  };

  const handleApproveUniform = async () => {
    if (!skill.skill_request?.uniforms?.[0]) return;

    setProcessingAction(true);
    setUniformMenuAnchor(null);

    try {
      const response = await axiosRubyClient.post(
        `/api/admin/worker_uniforms/${skill.skill_request.uniforms[0].id}/accept`,
      );

      if (response.data.success) {
        onUpdate();
      } else {
        setAlertDialog({
          open: true,
          title: 'Error',
          message: 'Failed to approve uniform',
        });
      }
    } catch (error) {
      console.error('Error approving uniform:', error);
      setAlertDialog({
        open: true,
        title: 'Error',
        message: 'Failed to approve uniform',
      });
    } finally {
      setProcessingAction(false);
    }
  };

  const handleRejectUniform = () => {
    if (!skill.skill_request?.uniforms?.[0]) return;
    setUniformMenuAnchor(null);

    setPromptDialog({
      open: true,
      title: 'Reject Uniform',
      message: 'Rejection reason (optional):',
      value: '',
      onConfirm: async (comment: string) => {
        setProcessingAction(true);

        try {
          const response = await axiosRubyClient.post(
            `/api/admin/worker_uniforms/${skill.skill_request!.uniforms![0].id}/reject`,
            { comment: comment || undefined },
          );

          if (response.data.success) {
            onUpdate();
          } else {
            setAlertDialog({
              open: true,
              title: 'Error',
              message: 'Failed to reject uniform',
            });
          }
        } catch (error) {
          console.error('Error rejecting uniform:', error);
          setAlertDialog({
            open: true,
            title: 'Error',
            message: 'Failed to reject uniform',
          });
        } finally {
          setProcessingAction(false);
        }
      },
    });
  };

  const handleReplaceUniform = () => {
    setUniformMenuAnchor(null);
    setShowUniformModal(true);
  };

  const handleApproveCertificate = async () => {
    if (!certificateMenuAnchor) return;

    const certId = certificateMenuAnchor.certificateId;
    setProcessingAction(true);
    setCertificateMenuAnchor(null);

    try {
      const response = await axiosRubyClient.post(
        `/api/admin/certificate_photos/${certId}/verify`,
      );

      if (response.data.success) {
        onUpdate();
      } else {
        setAlertDialog({
          open: true,
          title: 'Error',
          message: 'Failed to verify certificate',
        });
      }
    } catch (error) {
      console.error('Error verifying certificate:', error);
      setAlertDialog({
        open: true,
        title: 'Error',
        message: 'Failed to verify certificate',
      });
    } finally {
      setProcessingAction(false);
    }
  };

  const handleRejectCertificate = () => {
    if (!certificateMenuAnchor) return;

    const certId = certificateMenuAnchor.certificateId;
    setCertificateMenuAnchor(null);

    setPromptDialog({
      open: true,
      title: 'Reject Certificate',
      message: 'Rejection reason (optional):',
      value: '',
      onConfirm: async (comment: string) => {
        setProcessingAction(true);

        try {
          const response = await axiosRubyClient.post(
            `/api/admin/certificate_photos/${certId}/reject`,
            { comment: comment || undefined },
          );

          if (response.data.success) {
            onUpdate();
          } else {
            setAlertDialog({
              open: true,
              title: 'Error',
              message: 'Failed to reject certificate',
            });
          }
        } catch (error) {
          console.error('Error rejecting certificate:', error);
          setAlertDialog({
            open: true,
            title: 'Error',
            message: 'Failed to reject certificate',
          });
        } finally {
          setProcessingAction(false);
        }
      },
    });
  };

  const handleEditCertificateExpiry = () => {
    if (!certificateMenuAnchor) return;

    const cert = getSelectedCertificate();
    setCertificateMenuAnchor(null);

    setExpiryDialog({
      open: true,
      certificateId: certificateMenuAnchor.certificateId,
      expiryDate: cert?.expires_at
        ? new Date(cert.expires_at).toISOString().split('T')[0]
        : '',
    });
  };

  const handleSaveCertificateExpiry = async () => {
    if (!expiryDialog.certificateId) return;

    setProcessingAction(true);

    try {
      const response = await axiosRubyClient.patch(
        `/api/admin/certificate_photos/${expiryDialog.certificateId}`,
        {
          expires_at: expiryDialog.expiryDate || null,
        },
      );

      if (response.data.success) {
        setExpiryDialog({ open: false, certificateId: null, expiryDate: '' });
        onUpdate();
      } else {
        setAlertDialog({
          open: true,
          title: 'Error',
          message: 'Failed to update certificate expiry date',
        });
      }
    } catch (error) {
      console.error('Error updating certificate expiry:', error);
      setAlertDialog({
        open: true,
        title: 'Error',
        message: 'Failed to update certificate expiry date',
      });
    } finally {
      setProcessingAction(false);
    }
  };

  const getSelectedCertificate = () => {
    if (!certificateMenuAnchor || !skill.skill_request?.certificates)
      return null;
    return skill.skill_request.certificates.find(
      (c) => c.id === certificateMenuAnchor.certificateId,
    );
  };

  const handleToggleAssessment = async (passed: boolean) => {
    if (!skill.skill_request) return;

    setProcessingAction(true);

    try {
      const response = await axiosRubyClient.patch(
        `/api/admin/skill_requests/${skill.skill_request.id}/assessment`,
        { passed },
      );

      if (response.data.success) {
        onUpdate();
      } else {
        setAlertDialog({
          open: true,
          title: 'Error',
          message: `Failed to ${passed ? 'pass' : 'fail'} assessment`,
        });
      }
    } catch (error) {
      console.error('Error updating assessment:', error);
      setAlertDialog({
        open: true,
        title: 'Error',
        message: `Failed to ${passed ? 'pass' : 'fail'} assessment`,
      });
    } finally {
      setProcessingAction(false);
    }
  };

  const handleDeleteSkill = () => {
    if (!skill.worker_skill) return;
    setDeleteSkillDialog({ open: true, markAsIneligible: true });
  };

  const handleConfirmDeleteSkill = async () => {
    if (!skill.worker_skill) return;

    setDeleteSkillDialog({ ...deleteSkillDialog, open: false });
    setProcessingAction(true);

    try {
      const response = await axiosRubyClient.delete(
        `/api/admin/worker_skills/${skill.worker_skill.id}`,
        {
          data: {
            mark_as_ineligible: deleteSkillDialog.markAsIneligible,
          },
        },
      );

      if (response.data.success) {
        onUpdate();
      } else {
        setAlertDialog({
          open: true,
          title: 'Error',
          message: 'Failed to remove skill from worker',
        });
      }
    } catch (error) {
      console.error('Error removing skill:', error);
      setAlertDialog({
        open: true,
        title: 'Error',
        message: 'Failed to remove skill from worker',
      });
    } finally {
      setProcessingAction(false);
    }
  };

  const isApproved = skill.status === 'approved';

  return (
    <Stack vertical gap={24} style={{ width: '100%' }}>
      {/* Expiration Section - only show if not approved */}
      {skill.worker_skill && !isApproved && (
        <Card>
          <Card.Section>
            <ExpirationSection
              workerSkillId={skill.worker_skill.id}
              expiresAt={skill.worker_skill.expires_at}
              daysUntilExpiration={skill.worker_skill.days_until_expiration}
              expired={skill.worker_skill.expired}
              onUpdate={onUpdate}
            />
          </Card.Section>
        </Card>
      )}

      {/* Eligibility Section - TEMPORARILY HIDDEN
      {skill.skill_request && !isApproved && (
        <Card>
          <Card.Section>
            <Stack
              style={{
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
              }}
            >
              <Stack gap={8} style={{ alignItems: 'center' }}>
                <div
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: skill.skill_request.rejected_at
                      ? '#EF4444'
                      : '#10B981',
                  }}
                />
                <Body weight="semibold">Eligibility Status</Body>
              </Stack>
            </Stack>

            <Stack vertical gap={12}>
              {skill.skill_request.rejected_at ? (
                <>
                  <Badge label="Ineligible" status="danger" />
                  {skill.skill_request.rejection_comment && (
                    <Small color="lighter">
                      Reason: {skill.skill_request.rejection_comment}
                    </Small>
                  )}
                  <Small color="lighter">
                    Marked on{' '}
                    {new Date(
                      skill.skill_request.rejected_at,
                    ).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </Small>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleToggleRejection}
                    disabled={rejecting}
                  >
                    {rejecting ? 'Restoring...' : 'Restore Eligibility'}
                  </Button>
                </>
              ) : (
                <>
                  <Badge label="Eligible" status="theme" />
                  <Small color="lighter">
                    This worker is eligible for this skill
                  </Small>
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    onClick={handleToggleRejection}
                    disabled={rejecting}
                  >
                    {rejecting ? 'Marking...' : 'Mark as Ineligible'}
                  </Button>
                </>
              )}
            </Stack>
          </Card.Section>
        </Card>
      )}
      */}

      {/* Mark as Ineligible button - show for approved skills - TEMPORARILY HIDDEN
      {skill.skill_request && isApproved && (
        <Card>
          <Card.Section>
            <Stack
              style={{
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
              }}
            >
              <Stack gap={8} style={{ alignItems: 'center' }}>
                <div
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#10B981',
                  }}
                />
                <Body weight="semibold">Eligibility</Body>
              </Stack>
            </Stack>

            <Stack vertical gap={12}>
              <Badge label="Approved" status="theme" />
              <Small color="lighter">
                This skill has been approved for this worker
              </Small>
              <Button
                variant="outlined"
                size="small"
                color="error"
                onClick={handleToggleRejection}
                disabled={rejecting}
              >
                {rejecting ? 'Marking...' : 'Mark as Ineligible'}
              </Button>
            </Stack>
          </Card.Section>
        </Card>
      )}
      */}

      {/* Work Experience Section */}
      <Card>
        <Card.Section>
          <Stack
            style={{
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
            }}
          >
            <Stack gap={8} style={{ alignItems: 'center' }}>
              <div
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: '#10B981',
                }}
              />
              <Body weight="semibold">Work Experience</Body>
            </Stack>
            <Button size="small" onClick={handleAddExperience}>
              Add
            </Button>
          </Stack>

          {skill.skill_request?.work_experiences &&
          skill.skill_request.work_experiences.length > 0 ? (
            <Stack vertical gap={16}>
              {[...skill.skill_request.work_experiences]
                .sort(
                  (a, b) =>
                    new Date(b.start_date).getTime() -
                    new Date(a.start_date).getTime(),
                )
                .map((exp) => (
                  <Stack
                    key={exp.id}
                    style={{
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      paddingBottom: '16px',
                      borderBottom: '1px solid #E5E7EB',
                    }}
                  >
                    <Stack vertical gap={4}>
                      <Body weight="semibold">{exp.company_name}</Body>
                      <Small color="lighter">
                        {(() => {
                          // Parse YYYY-MM-DD as local date to avoid timezone shifts
                          const [year, month] = exp.start_date.split('-');
                          return new Date(
                            parseInt(year),
                            parseInt(month) - 1,
                          ).toLocaleDateString('en-US', {
                            month: 'short',
                            year: 'numeric',
                          });
                        })()}{' '}
                        -{' '}
                        {exp.end_date
                          ? (() => {
                              const [year, month] = exp.end_date.split('-');
                              return new Date(
                                parseInt(year),
                                parseInt(month) - 1,
                              ).toLocaleDateString('en-US', {
                                month: 'short',
                                year: 'numeric',
                              });
                            })()
                          : 'Present'}
                      </Small>
                      {exp.is_staffing_agency && (
                        <Small color="lighter">Through Staffing agency</Small>
                      )}
                    </Stack>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, exp.id)}
                    >
                      <MoreVert fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
            </Stack>
          ) : (
            <Small color="lighter">No work experience added yet</Small>
          )}
        </Card.Section>
      </Card>

      {/* Work Experience Menu */}
      <Menu
        anchorEl={menuAnchor?.element}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditExperience}>Edit</MenuItem>
        <MenuItem onClick={handleDeleteExperience} sx={{ color: 'error.main' }}>
          Delete
        </MenuItem>
      </Menu>

      {/* Uniform Menu */}
      <Menu
        anchorEl={uniformMenuAnchor}
        open={Boolean(uniformMenuAnchor)}
        onClose={() => setUniformMenuAnchor(null)}
      >
        <MenuItem
          onClick={handleApproveUniform}
          disabled={
            processingAction ||
            skill.skill_request?.uniforms?.[0]?.status === 'accepted'
          }
        >
          Approve
        </MenuItem>
        <MenuItem
          onClick={handleRejectUniform}
          disabled={
            processingAction ||
            skill.skill_request?.uniforms?.[0]?.status === 'rejected'
          }
          sx={{ color: 'error.main' }}
        >
          Reject
        </MenuItem>
        <MenuItem onClick={handleReplaceUniform} disabled={processingAction}>
          Replace
        </MenuItem>
      </Menu>

      {/* Certificate Menu */}
      <Menu
        anchorEl={certificateMenuAnchor?.element}
        open={Boolean(certificateMenuAnchor)}
        onClose={() => setCertificateMenuAnchor(null)}
      >
        <MenuItem
          onClick={handleApproveCertificate}
          disabled={
            processingAction || getSelectedCertificate()?.status === 'verified'
          }
        >
          Verify
        </MenuItem>
        <MenuItem
          onClick={handleRejectCertificate}
          disabled={
            processingAction || getSelectedCertificate()?.status === 'rejected'
          }
          sx={{ color: 'error.main' }}
        >
          Reject
        </MenuItem>
        <MenuItem
          onClick={handleEditCertificateExpiry}
          disabled={processingAction}
        >
          Edit Expiry
        </MenuItem>
      </Menu>

      {/* Skill Assessment Section */}
      {skill.requires_assessment && skill.skill_request && (
        <Card>
          <Card.Section>
            <Stack
              style={{
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
              }}
            >
              <Stack gap={8} style={{ alignItems: 'center' }}>
                <div
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#10B981',
                  }}
                />
                <Body weight="semibold">
                  Skill Assessment: Multiple Choice Quiz
                </Body>
              </Stack>
            </Stack>

            <Stack vertical gap={16}>
              {skill.skill_request.assessment ? (
                <>
                  <Badge
                    label={
                      skill.skill_request.assessment_passed
                        ? 'Passed'
                        : 'Failed'
                    }
                    status={
                      skill.skill_request.assessment_passed ? 'theme' : 'danger'
                    }
                  />
                  {skill.skill_request.assessment.passed_at && (
                    <Small color="lighter">
                      {skill.skill_request.assessment_passed
                        ? 'Passed'
                        : 'Failed'}{' '}
                      on{' '}
                      {new Date(
                        skill.skill_request.assessment.passed_at,
                      ).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </Small>
                  )}
                  <Stack gap={8}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => handleToggleAssessment(true)}
                      disabled={
                        processingAction ||
                        skill.skill_request?.assessment_passed
                      }
                    >
                      Mark as Passed
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      onClick={() => handleToggleAssessment(false)}
                      disabled={
                        processingAction ||
                        !skill.skill_request?.assessment_passed
                      }
                    >
                      Mark as Failed
                    </Button>
                  </Stack>
                </>
              ) : (
                <Small color="lighter">No assessment taken yet</Small>
              )}
            </Stack>
          </Card.Section>
        </Card>
      )}

      {/* AI Assessment Section */}
      {skill.ai_assessment && (
        <Card>
          <Card.Section>
            <Stack
              style={{
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
              }}
            >
              <Stack gap={8} style={{ alignItems: 'center' }}>
                <div
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor:
                      skill.ai_assessment.final_status === 'passed'
                        ? '#10B981'
                        : skill.ai_assessment.final_status === 'failed'
                          ? '#EF4444'
                          : skill.ai_assessment.final_status === 'for_review'
                            ? '#F59E0B'
                            : '#6B7280',
                  }}
                />
                <Body weight="semibold">AI Assessment</Body>
              </Stack>
            </Stack>

            <Stack vertical gap={12}>
              <Stack gap={8} style={{ alignItems: 'center' }}>
                <Badge
                  label={
                    skill.ai_assessment.final_status === 'passed'
                      ? 'Passed'
                      : skill.ai_assessment.final_status === 'failed'
                        ? 'Failed'
                        : skill.ai_assessment.final_status === 'for_review'
                          ? 'For Review'
                          : 'Pending'
                  }
                  status={
                    skill.ai_assessment.final_status === 'passed'
                      ? 'theme'
                      : skill.ai_assessment.final_status === 'failed'
                        ? 'danger'
                        : skill.ai_assessment.final_status === 'for_review'
                          ? 'warning'
                          : 'neutral'
                  }
                />
                {skill.ai_assessment.status !== 'completed' &&
                  skill.ai_assessment.status !== 'failed' && (
                    <Badge
                      label={
                        skill.ai_assessment.status === 'recording'
                          ? 'Recording'
                          : skill.ai_assessment.status === 'transcribing'
                            ? 'Transcribing'
                            : skill.ai_assessment.status === 'scoring'
                              ? 'Scoring'
                              : 'In Progress'
                      }
                      status="neutral"
                    />
                  )}
              </Stack>

              {skill.ai_assessment.total_score !== null && (
                <Small>
                  Score: {skill.ai_assessment.total_score.toFixed(1)}%
                </Small>
              )}

              {skill.ai_assessment.started_at && (
                <Small color="lighter">
                  Started:{' '}
                  {new Date(skill.ai_assessment.started_at).toLocaleDateString(
                    'en-US',
                    {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    },
                  )}
                </Small>
              )}

              {skill.ai_assessment.completed_at && (
                <Small color="lighter">
                  Completed:{' '}
                  {new Date(
                    skill.ai_assessment.completed_at,
                  ).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                </Small>
              )}

              {skill.ai_assessment.reviewed_at && (
                <Small color="lighter">
                  Reviewed:{' '}
                  {new Date(skill.ai_assessment.reviewed_at).toLocaleDateString(
                    'en-US',
                    {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    },
                  )}
                  {skill.ai_assessment.reviewed_by_name &&
                    ` by ${skill.ai_assessment.reviewed_by_name}`}
                </Small>
              )}
            </Stack>
          </Card.Section>
        </Card>
      )}

      {/* Uniform Section */}
      {skill.requires_uniform && skill.skill_request && (
        <Card>
          <Card.Section>
            <Stack
              style={{
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
              }}
            >
              <Stack gap={8} style={{ alignItems: 'center' }}>
                <div
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#10B981',
                  }}
                />
                <Body weight="semibold">Black & White Uniform</Body>
              </Stack>
              {skill.skill_request.uniforms &&
                skill.skill_request.uniforms.length > 0 && (
                  <IconButton
                    size="small"
                    onClick={(e) => setUniformMenuAnchor(e.currentTarget)}
                  >
                    <MoreVert fontSize="small" />
                  </IconButton>
                )}
            </Stack>

            <Stack vertical gap={16}>
              <Small color="lighter">
                Clean and pressed. White long-sleeve button up shirt, black
                slacks, black socks, black non-slip shoes.
              </Small>

              {skill.skill_request.uniforms &&
              skill.skill_request.uniforms.length > 0 ? (
                <Stack vertical gap={12}>
                  <Small weight="semibold">Submitted Uniform</Small>
                  <img
                    src={skill.skill_request.uniforms[0].image_url}
                    alt="Worker uniform"
                    style={{
                      width: '100%',
                      maxWidth: '400px',
                      height: 'auto',
                      borderRadius: '8px',
                      objectFit: 'cover',
                    }}
                  />
                  <Stack vertical gap={4}>
                    <Small>
                      Status:{' '}
                      {skill.skill_request.uniforms[0].status === 'accepted'
                        ? 'Approved'
                        : skill.skill_request.uniforms[0].status === 'rejected'
                          ? 'Rejected'
                          : 'Pending Approval'}
                    </Small>
                    <Small color="lighter">
                      Uploaded on{' '}
                      {new Date(
                        skill.skill_request.uniforms[0].created_at,
                      ).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                    </Small>
                  </Stack>
                </Stack>
              ) : (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Add />}
                  onClick={() => setShowUniformModal(true)}
                >
                  Upload Uniform Photo
                </Button>
              )}
            </Stack>
          </Card.Section>
        </Card>
      )}

      {/* Certificate Section */}
      {skill.requires_certificate && skill.skill_request && (
        <Card>
          <Card.Section>
            <Stack
              style={{
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
              }}
            >
              <Stack gap={8} style={{ alignItems: 'center' }}>
                <div
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#6B7280',
                  }}
                />
                <Body weight="semibold">Certificates</Body>
              </Stack>
              <Button
                size="small"
                onClick={() => setShowCertificateModal(true)}
              >
                Add
              </Button>
            </Stack>

            <Stack vertical gap={16}>
              {skill.skill_request.certificates &&
              skill.skill_request.certificates.length > 0 ? (
                <Stack vertical gap={24}>
                  {skill.skill_request.certificates.map((cert, index) => (
                    <Stack
                      key={cert.id}
                      style={{
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        paddingBottom:
                          index < skill.skill_request!.certificates!.length - 1
                            ? '16px'
                            : '0',
                        borderBottom:
                          index < skill.skill_request!.certificates!.length - 1
                            ? '1px solid #E5E7EB'
                            : 'none',
                      }}
                    >
                      <Stack vertical gap={12} style={{ flex: 1 }}>
                        <Small weight="semibold">
                          {cert.type
                            ? cert.type
                                .replace(/_/g, ' ')
                                .replace(/\b\w/g, (c) => c.toUpperCase())
                            : `Certificate ${index + 1}`}
                          {index === 0 && ' (Latest)'}
                        </Small>
                        <img
                          src={cert.image_url}
                          alt={`Worker certificate ${index + 1}`}
                          style={{
                            width: '100%',
                            maxWidth: '400px',
                            height: 'auto',
                            borderRadius: '8px',
                            objectFit: 'cover',
                            border: '1px solid #E5E7EB',
                          }}
                        />
                        <Stack vertical gap={4}>
                          <Small>
                            Status:{' '}
                            {cert.status === 'verified'
                              ? 'Verified'
                              : cert.status === 'rejected'
                                ? 'Rejected'
                                : 'Pending Verification'}
                          </Small>
                          {cert.expires_at && (
                            <Small
                              color={
                                new Date(cert.expires_at) < new Date()
                                  ? 'danger'
                                  : 'lighter'
                              }
                            >
                              Expires:{' '}
                              {new Date(cert.expires_at).toLocaleDateString(
                                'en-US',
                                {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                },
                              )}
                              {new Date(cert.expires_at) < new Date() &&
                                ' (Expired)'}
                            </Small>
                          )}
                          <Small color="lighter">
                            Uploaded on{' '}
                            {new Date(cert.created_at).toLocaleDateString(
                              'en-US',
                              {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit',
                              },
                            )}
                          </Small>
                        </Stack>
                      </Stack>
                      <IconButton
                        size="small"
                        onClick={(e) =>
                          setCertificateMenuAnchor({
                            element: e.currentTarget,
                            certificateId: cert.id,
                          })
                        }
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}
                </Stack>
              ) : (
                <Small color="lighter">No certificates uploaded yet</Small>
              )}
            </Stack>
          </Card.Section>
        </Card>
      )}

      {/* Remove Skill Section */}
      {skill.worker_skill && (
        <Card>
          <Card.Section>
            <Stack
              style={{
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
              }}
            >
              <Stack gap={8} style={{ alignItems: 'center' }}>
                <div
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#EF4444',
                  }}
                />
                <Body weight="semibold">Danger Zone</Body>
              </Stack>
            </Stack>

            <Stack vertical gap={12}>
              <Small color="lighter">
                Removing this skill will prevent the worker from being assigned
                to jobs requiring this skill.
              </Small>
              <Button
                variant="outlined"
                size="small"
                color="error"
                onClick={handleDeleteSkill}
                disabled={processingAction}
              >
                {processingAction ? 'Removing...' : 'Remove Skill from Worker'}
              </Button>
            </Stack>
          </Card.Section>
        </Card>
      )}

      <AddWorkExperienceModal
        open={showAddModal}
        workerId={workerId}
        skillId={skill.skill_id}
        skillRequestId={skill.skill_request?.id}
        experienceId={editingExperience}
        onClose={() => setShowAddModal(false)}
        onSuccess={handleExperienceAdded}
      />
      <UploadUniformModal
        open={showUniformModal}
        workerId={workerId}
        skillId={skill.skill_id}
        skillRequestId={skill.skill_request?.id}
        onClose={() => setShowUniformModal(false)}
        onSuccess={() => {
          setShowUniformModal(false);
          onUpdate();
        }}
      />
      <UploadCertificateModal
        open={showCertificateModal}
        workerId={workerId}
        skillId={skill.skill_id}
        skillRequestId={skill.skill_request?.id}
        certificateType={
          skill.skill_request?.certificates?.[0]?.type || undefined
        }
        onClose={() => setShowCertificateModal(false)}
        onSuccess={() => {
          setShowCertificateModal(false);
          onUpdate();
        }}
      />

      {/* Confirm Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmDialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setConfirmDialog({ ...confirmDialog, open: false });
              confirmDialog.onConfirm();
            }}
            variant="contained"
            color="primary"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Prompt Dialog */}
      <Dialog
        open={promptDialog.open}
        onClose={() => setPromptDialog({ ...promptDialog, open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{promptDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText style={{ marginBottom: '16px' }}>
            {promptDialog.message}
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            value={promptDialog.value}
            onChange={(e) =>
              setPromptDialog({ ...promptDialog, value: e.target.value })
            }
            placeholder="Enter reason..."
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPromptDialog({ ...promptDialog, open: false })}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              const value = promptDialog.value;
              setPromptDialog({ ...promptDialog, open: false, value: '' });
              promptDialog.onConfirm(value);
            }}
            variant="contained"
            color="primary"
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Dialog */}
      <Dialog
        open={alertDialog.open}
        onClose={() => setAlertDialog({ ...alertDialog, open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{alertDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{alertDialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAlertDialog({ ...alertDialog, open: false })}
            variant="contained"
            color="primary"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Skill Dialog */}
      <Dialog
        open={deleteSkillDialog.open}
        onClose={() =>
          setDeleteSkillDialog({ ...deleteSkillDialog, open: false })
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Remove Skill from Worker</DialogTitle>
        <DialogContent>
          <DialogContentText style={{ marginBottom: '16px' }}>
            Are you sure you want to remove "{skill.skill_name}" from this
            worker? This action cannot be undone.
          </DialogContentText>
          <FormControlLabel
            control={
              <Checkbox
                checked={deleteSkillDialog.markAsIneligible}
                onChange={(e) =>
                  setDeleteSkillDialog({
                    ...deleteSkillDialog,
                    markAsIneligible: e.target.checked,
                  })
                }
                color="primary"
              />
            }
            label="Also mark as ineligible for this skill"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setDeleteSkillDialog({ ...deleteSkillDialog, open: false })
            }
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteSkill}
            variant="contained"
            color="error"
          >
            Remove Skill
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Certificate Expiry Dialog */}
      <Dialog
        open={expiryDialog.open}
        onClose={() =>
          setExpiryDialog({ open: false, certificateId: null, expiryDate: '' })
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Certificate Expiry Date</DialogTitle>
        <DialogContent>
          <DialogContentText style={{ marginBottom: '16px' }}>
            Set the expiration date for this certificate.
          </DialogContentText>
          <TextField
            type="date"
            fullWidth
            value={expiryDialog.expiryDate}
            onChange={(e) =>
              setExpiryDialog({ ...expiryDialog, expiryDate: e.target.value })
            }
            InputLabelProps={{ shrink: true }}
            label="Expiry Date"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setExpiryDialog({
                open: false,
                certificateId: null,
                expiryDate: '',
              })
            }
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveCertificateExpiry}
            variant="contained"
            color="primary"
            disabled={processingAction}
          >
            {processingAction ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default SkillPanel;
